import React, { useMemo } from 'react';

import { useLpsChannelSummary } from '../use-lps';

import type { LpsAssignedWindow } from './LpsDestinationSummary';
import type { LpsLeadSplit, LpsLeadType } from '../lps.api';

import { isoDate } from '@/features/leads/lead-date-range';

/** A zero reads as clutter in a grid this dense; an empty cell reads as "none". */
const cell = (n: number): React.ReactNode =>
  n === 0 ? <span className="text-zinc-300">-</span> : n;

const border = 'border border-zinc-200';
const head = `${border} bg-zinc-50 px-3 py-1.5 text-center font-medium whitespace-nowrap`;
const num = `${border} px-3 py-2 text-center tabular-nums`;

/** "instagram" -> "Instagram"; the classifier hands back lowercase keys. */
/** Platforms whose name is not just a capitalised word. */
const PLATFORM_LABEL: Record<string, string> = { 'utazzo crm': 'Utazzo CRM' };

const platformLabel = (platform: string | null): string =>
  platform
    ? (PLATFORM_LABEL[platform] ?? platform.charAt(0).toUpperCase() + platform.slice(1))
    : 'Other';

const EMPTY: LpsLeadSplit = { total: 0, paid: 0, organic: 0 };

/**
 * Which platform sends leads for which destination: destinations down the
 * side, platforms across the top. Same window and lead-type switch as the
 * destination table above it, so the two always describe the same leads.
 *
 * On the All tab every platform splits into an Inorganic / Organic pair; on
 * the other two tabs the split would be all one side, so one column does.
 */
export const LpsChannelSummary = ({
  window,
  leadType,
  variant = 'lps',
}: {
  window: LpsAssignedWindow;
  leadType: LpsLeadType | 'all';
  /** 'marketing' reads the same data through the Marketing Report's endpoint and
   *  permission; default 'lps' is the native Lead Parsing System view. */
  variant?: 'lps' | 'marketing';
}): React.JSX.Element => {
  const from = isoDate(window.range.from);
  const to = isoDate(window.range.to);
  const query = useMemo(
    () => ({
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(leadType !== 'all' ? { leadType } : {}),
    }),
    [from, to, leadType],
  );
  const summary = useLpsChannelSummary(
    query,
    variant === 'marketing' ? '/reports/marketing/leads-by-channel' : undefined,
  );

  const columns = summary.data?.platforms ?? [];
  const rows = summary.data?.destinations ?? [];
  const keyOf = (platform: string | null): string => platform ?? '';
  const split = leadType === 'all';

  /** One cell per column on a filtered tab, a paid + organic pair on All. */
  const cells = (n: LpsLeadSplit, strong = false): React.ReactNode => {
    const cls = strong ? `${num} font-semibold` : num;
    return split ? (
      <>
        <td className={cls}>{cell(n.paid)}</td>
        <td className={cls}>{cell(n.organic)}</td>
      </>
    ) : (
      <td className={cls}>{strong ? n.total : cell(n.total)}</td>
    );
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-4">
      <header className="mb-3">
        <h2 className="text-sm font-semibold">Leads by destination and channel</h2>
        <p className="text-xs text-zinc-500">
          {summary.data
            ? split
              ? `${summary.data.total} leads received: ${summary.data.paid} inorganic, ${summary.data.organic} organic.`
              : `${summary.data.total} leads received, by the platform they came from.`
            : 'Counts per destination for each platform.'}
        </p>
      </header>

      {summary.isError && (
        <p className="text-sm text-red-700">Could not load the channel breakdown.</p>
      )}
      {!summary.isError && rows.length === 0 && (
        <p className="text-sm text-zinc-500">
          {summary.isLoading
            ? 'Loading...'
            : !from
              ? 'No leads yet.'
              : `No leads ${from} to ${to ?? from}. Pick a longer period to see older leads.`}
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-xs font-semibold text-zinc-700">
                <th
                  rowSpan={split ? 2 : 1}
                  className={`sticky left-0 z-10 ${border} bg-zinc-50 px-3 py-2 text-left align-middle`}
                >
                  Destination
                </th>
                {columns.map((c) => (
                  <th key={keyOf(c.platform)} colSpan={split ? 2 : 1} className={head}>
                    {platformLabel(c.platform)}
                  </th>
                ))}
                <th colSpan={split ? 2 : 1} className={head}>
                  Total
                </th>
              </tr>
              {split && (
                <tr className="text-[11px] font-medium text-zinc-500">
                  {[...columns.map((c) => keyOf(c.platform)), '__total'].map((k) => (
                    <React.Fragment key={k}>
                      <th className={head}>Inorganic</th>
                      <th className={head}>Organic</th>
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.destination ?? 'none'}>
                  <td
                    className={`sticky left-0 z-10 ${border} bg-white px-3 py-2 font-medium whitespace-nowrap`}
                  >
                    {row.destination ?? <span className="text-amber-700">No destination</span>}
                  </td>
                  {columns.map((c) => (
                    <React.Fragment key={keyOf(c.platform)}>
                      {cells(row.counts[keyOf(c.platform)] ?? EMPTY)}
                    </React.Fragment>
                  ))}
                  {cells(row, true)}
                </tr>
              ))}
              <tr className="bg-emerald-50 font-semibold text-emerald-700">
                <td
                  className={`sticky left-0 z-10 ${border} bg-emerald-50 px-3 py-2 text-zinc-900`}
                >
                  Total
                </td>
                {columns.map((c) => (
                  <React.Fragment key={keyOf(c.platform)}>{cells(c, true)}</React.Fragment>
                ))}
                {cells(summary.data ?? EMPTY, true)}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
